<template>
  <div class="audio-player">
    <p class="audio-player__description">
      <img :src="headphoneIcon" />
      {{ c('audio.play_clip') }}
    </p>
    <audio ref="playerRef" :src="src" @ended="$emit('ended')" />
    <button
      class="attest-button attest-button--styleless audio-player__play-pause-button"
      @click="playPauseButton.onClick"
    >
      <img
        class="audio-player__play-pause-icon"
        :src="playPauseButton.icon"
        :alt="playPauseButton.alt"
      />
    </button>
    <div class="audio-player__progress">
      <ProgressBar :value="progress" />
      {{ remainingTime }}
    </div>
  </div>
</template>

<script lang="ts">
import { isUndefined } from 'ramda-adjunct'
import { computed, defineComponent, reactive, ref, type Ref } from 'vue'

import { c } from '../../../intl'
import headphoneIcon from '../../../shared/assets/headphone-white.svg'
import ProgressBar from '../../../shared/components/dumb/ProgressBar.vue'
import {
  useAudioProgress,
  useAudioRemainingSeconds,
  useAudioState,
} from '../../../shared/composables/useAudio'
import pauseButton from '../../assets/pause-button.svg'
import playButton from '../../assets/play-button.svg'

const useRemainingTime = (
  audioElRef: Readonly<Ref<HTMLAudioElement | undefined>>,
): Readonly<Ref<string>> => {
  const remainingSeconds = useAudioRemainingSeconds(audioElRef)
  return computed(() => {
    const [min, sec] = [
      Math.floor(remainingSeconds.value / 60),
      Math.round(remainingSeconds.value % 60),
    ]
    return `-${min}:${sec}`
  })
}

export default defineComponent({
  components: { ProgressBar },
  props: {
    src: {
      type: String,
      required: true,
    },
  },
  emits: {
    ended: () => true,
  },
  setup() {
    const playerRef = ref<HTMLAudioElement>()
    const state = useAudioState(playerRef)

    return {
      headphoneIcon,
      playerRef,
      progress: useAudioProgress(playerRef),
      playPauseButton: reactive({
        icon: computed(() => (state.value === 'PLAYING' ? pauseButton : playButton)),
        alt: computed(() => (state.value === 'PLAYING' ? 'pause' : 'play')),
        onClick: (): void => {
          const player = playerRef.value
          if (isUndefined(player)) {
            return
          }
          player[player.paused ? 'play' : 'pause']()
        },
      }),
      remainingTime: useRemainingTime(playerRef),
      c,
    }
  },
})
</script>

<style lang="postcss">
.audio-player {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  &__description {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }

  &__progress {
    display: flex;
    width: 100%;
    max-width: max(calc(100% - 4rem), 20rem);
    flex-direction: column;
    align-items: flex-end;
    gap: 1rem;
  }

  &__play-pause-button,
  &__play-pause-icon {
    --play-button-size: 6rem;
    width: var(--play-button-size);
    height: var(--play-button-size);
  }
}
</style>
